import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Modal,
  Upload,
  Button,
  message,
  Table,
  Form,
  Input,
  Tooltip,
  Typography,
  Space,
  Select,
  DatePicker,
  Spin,
} from "antd";
import {
  InboxOutlined,
  WarningOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

import axios from "axios";
import * as XLSX from "xlsx";
import { createTransaction } from "../../../../Services/TransactionService";
import { checkInternetBeforeAPI } from "../../../../Utils/NetworkUtils";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { FETCH_ALL_ARTICLE_API } from "../../../../Constants/Apis";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(customParseFormat);

const { Text } = Typography;
const { Dragger } = Upload;

/** ---------- helpers & constants ---------- */
const normalize = (v) => (v ?? "").toString().trim();
// tweak for your barcode formats
const isLikelyBarcode = (v) => /^[A-Za-z0-9._-]{6,}$/.test(normalize(v));
const sanitizeText = (s) =>
  String(s ?? "")
    .replace(/^[=+\-@]/, "") // prevent formula-like text
    .replace(/[\u0000-\u001F\u007F]/g, ""); // strip control chars

const uid = () =>
  `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

const ALLOWED_MARKETPLACES = new Set([
  "Shopee",
  "Tokopedia",
  "Lazada",
  "TikTok",
  "Blibli",
  "Manual",
  "Other",
]);
const MAX_QTY = 1_000_000;
const MAX_AGE_DAYS = 30;

/** currency parsing that handles both , and . conventions */
const parseCurrency = (v) => {
  if (v == null || v === "") return 0;
  let s = String(v).trim();

  if (s.includes(",") && s.includes(".")) {
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    const dec = lastComma > lastDot ? "," : ".";
    const thou = dec === "," ? "." : ",";
    s = s.split(thou).join("");
    s = s.replace(dec, ".");
  } else if (s.includes(",")) {
    s = s.split(".").join("");
    s = s.replace(",", ".");
  } else {
    const parts = s.split(".");
    if (parts.length > 2) s = parts.join("");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

/** merge duplicates by (noBon, barcode, marketplace, transactionDate) */
const dedupeRows = (rows) => {
  const keyOf = (r) =>
    [
      normalize(r.noBon),
      normalize(r.barcode),
      normalize(r.marketplace).toLowerCase(),
      normalize(r.transactionDate),
    ].join("|");
  const map = new Map();
  for (const r of rows) {
    const k = keyOf(r);
    if (!map.has(k)) {
      map.set(k, { ...r });
    } else {
      const cur = map.get(k);
      cur.quantity = (Number(cur.quantity) || 0) + (Number(r.quantity) || 0);
      cur.total = (Number(cur.total) || 0) + (Number(r.total) || 0);
    }
  }
  return [...map.values()];
};

const BulkInputForm = ({
  isVisible,
  onCancel,
  stockData = [],
  userData = {},
}) => {
  const [fileData, setFileData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [editingKey, setEditingKey] = useState("");
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [shouldResetData, setShouldResetData] = useState(false);
  const [parsing, setParsing] = useState(false);

  /** ---- fast lookups ---- */
  const stockByBarcode = useMemo(() => {
    const m = new Map();
    (stockData || []).forEach(
      (s) => s?.barcode && m.set(normalize(s.barcode), s)
    );
    return m;
  }, [stockData]);

  const stockBySku = useMemo(() => {
    const m = new Map();
    (stockData || []).forEach((s) => {
      if (s?.sku != null) {
        const sku = normalize(String(s.sku));
        m.set(sku, s);
        m.set(sku.replace(/^0+/, ""), s); // handle Excel stripped zeros
      }
    });
    return m;
  }, [stockData]);

  const totalAvailable = useCallback(
    (barcode) => {
      const bc = normalize(barcode);
      return (stockData || [])
        .filter((i) => normalize(i.barcode) === bc && i.status === "in stock")
        .reduce((sum, i) => sum + Number(i.currentQuantity || 0), 0);
    },
    [stockData]
  );

  /** ---- cache for article lookups ---- */
  const articleCache = useRef(new Map());
  const fetchArticleByBarcodePrefix = async (barcodePrefix) => {
    if (!barcodePrefix) return null;
    if (articleCache.current.has(barcodePrefix))
      return articleCache.current.get(barcodePrefix);
    try {
      const response = await axios.get(
        `${FETCH_ALL_ARTICLE_API}/dropdown?searchKeyword=${barcodePrefix}`
      );
      const articles = response?.data?.data || [];
      const found =
        articles.find((art) => art?.preBarcode === barcodePrefix) ||
        articles.find((art) => art?.preBarcode?.includes(barcodePrefix)) ||
        null;
      articleCache.current.set(barcodePrefix, found);
      return found;
    } catch (error) {
      console.error("Error fetching article data:", error);
      return null;
    }
  };

  const determinePriceFromArticle = (article, stockItem) => {
    if (!article?.prices?.length) return Number(stockItem.price) || 0;
    const counterDeptId = userData?.deptId;
    const stockColorId = stockItem.colorId;
    const specificPrice = article.prices.find(
      (p) => p?.deptId === counterDeptId && p?.colorId === stockColorId
    );
    const deptPrice = article.prices.find(
      (p) => p?.deptId === counterDeptId && !p?.colorId
    );
    return (
      Number(specificPrice?.harga_internal) ||
      Number(deptPrice?.harga_internal) ||
      Number(article.prices[0]?.harga_internal) ||
      Number(stockItem.price) ||
      0
    );
  };

  /** ---- lifecycle resets ---- */
  useEffect(() => {
    if (shouldResetData) {
      setFileData([]);
      setValidationResults([]);
      setEditingKey("");
      setSubmitting(false);
      form.resetFields();
      setShouldResetData(false);
    }
  }, [shouldResetData, form]);

  useEffect(() => {
    return () => {
      setFileData([]);
      setValidationResults([]);
      setEditingKey("");
      setSubmitting(false);
    };
  }, []);

  /** ---- validation per row ---- */
  const validateRow = (row) => {
    const errors = [];

    if (!row || typeof row !== "object") {
      errors.push("Invalid row data");
      return { isValid: false, errors, isBermasalah: false };
    }

    // normalize a few
    row.noBon = sanitizeText(row.noBon);
    row.marketplace = sanitizeText(row.marketplace);
    row.keterangan = sanitizeText(row.keterangan);
    row.barcode = normalize(row.barcode);

    // sku/barcode existence & syntax
    if (!row.barcode || !isLikelyBarcode(row.barcode)) {
      errors.push("SKU/Barcode invalid");
    }

    const stockItem = stockByBarcode.get(row.barcode);
    if (!stockItem) {
      errors.push("SKU/Barcode not found in inventory");
    }

    // quantity checks
    const q = Number(row.quantity);
    if (!Number.isInteger(q) || q <= 0 || q > MAX_QTY) {
      errors.push(`Quantity must be integer 1..${MAX_QTY}`);
    }

    // total if present
    if (row.total !== undefined && row.total !== null && row.total !== "") {
      const t = parseCurrency(row.total);
      if (!Number.isFinite(t) || t < 0) {
        errors.push("Total must be a positive number");
      } else {
        row.total = t;
      }
    }

    // marketplace whitelist
    if (!row.marketplace) {
      errors.push("Marketplace is required");
    } else if (!ALLOWED_MARKETPLACES.has(row.marketplace)) {
      errors.push("Marketplace is invalid");
    }

    // order id
    if (!row.noBon) errors.push("Order ID is required");

    // date handling
    if (row.transactionDate) {
      try {
        let dt = null;
        if (dayjs.isDayjs(row.transactionDate)) dt = row.transactionDate;
        else if (row.transactionDate instanceof Date)
          dt = dayjs(row.transactionDate);
        else if (typeof row.transactionDate === "string") {
          // try strict formats first
          const formats = [
            "YYYY-MM-DD",
            "DD-MM-YYYY HH:mm",
            "YYYY-MM-DD HH:mm",
            "DD/MM/YYYY",
            "MM/DD/YYYY",
            "YYYY/MM/DD",
            "DD-MM-YYYY",
            "MM-DD-YYYY",
            "DD.MM.YYYY",
            "MM.DD.YYYY",
          ];
          let ok = null;
          for (const f of formats) {
            const test = dayjs(row.transactionDate, f, true);
            if (test.isValid()) {
              ok = test;
              break;
            }
          }
          dt = ok || dayjs(row.transactionDate);
        }
        if (dt && dt.isValid()) {
          const now = dayjs().endOf("day");
          const earliest = dayjs().subtract(MAX_AGE_DAYS, "day").startOf("day");
          if (dt.isBefore(earliest) || dt.isAfter(now)) {
            errors.push(
              `Transaction date must be within the last ${MAX_AGE_DAYS} days`
            );
          } else {
            row.transactionDate = dt.format("YYYY-MM-DD");
          }
        } else {
          errors.push("Invalid transaction date");
          row.transactionDate = String(row.transactionDate);
        }
      } catch {
        errors.push("Invalid transaction date");
        row.transactionDate = String(row.transactionDate);
      }
    } else {
      // default to today if entirely missing
      row.transactionDate = dayjs().format("YYYY-MM-DD");
    }

    // oversell flag (with total available, not single lot)
    const isBermasalah = stockItem && q > totalAvailable(row.barcode);

    return {
      isValid: errors.length === 0,
      errors,
      isBermasalah,
    };
  };

  const validateAllRows = (data) =>
    data.map((row) => {
      const validation = validateRow(row);
      return {
        ...row,
        key: row.key || uid(),
        isValid: validation.isValid,
        errors: validation.errors,
        isBermasalah: validation.isBermasalah,
      };
    });

  /** ---- upload & parse ---- */
  const handleUpload = (file) => {
    const accepted = [".xlsx", ".xls", ".csv"];
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!accepted.includes(ext)) {
      message.error("Please upload a valid Excel or CSV file");
      return false;
    }

    const reader = new FileReader();
    setParsing(true);

    reader.onload = (e) => {
      try {
        if (!e?.target?.result) throw new Error("Failed to read file contents");
        const workbook = XLSX.read(e.target.result, { type: "array" });

        if (!workbook?.SheetNames?.length)
          throw new Error("Empty or invalid workbook");
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) throw new Error("Worksheet is empty or invalid");

        const raw = XLSX.utils.sheet_to_json(worksheet, {
          raw: false,
          dateNF: "YYYY-MM-DD",
          cellDates: true,
        });
        if (!raw?.length) throw new Error("No data found in the uploaded file");

        const columnMappings = {
          "tanggal pembuatan": "transactionDate",
          "id pesanan": "noBon",
          channel: "marketplace",
          sku: "sku",
          "harga total promosi": "total",
          subtotal: "totalPrice",
          jumlah: "quantity",
        };

        const normalizedData = raw
          .map((row, idx) => {
            if (!row || typeof row !== "object") return null;

            const normalizedRow = {};
            let foundMapped = false;
            const keys = Object.keys(row);

            keys.forEach((key) => {
              const normKey = key?.toLowerCase?.().trim?.();
              if (!normKey) return;

              let mappedKey = null;
              Object.keys(columnMappings).forEach((k) => {
                if (normKey === k) mappedKey = columnMappings[k];
              });
              if (!mappedKey) return;

              foundMapped = true;

              if (mappedKey === "sku") {
                const skuValue = normalize(row[key]);
                const sStock =
                  stockBySku.get(skuValue) ||
                  stockBySku.get(skuValue.replace(/^0+/, ""));
                normalizedRow.sku = skuValue;
                normalizedRow.barcode = sStock?.barcode
                  ? normalize(sStock.barcode)
                  : isLikelyBarcode(skuValue)
                  ? skuValue
                  : "";
              } else if (mappedKey === "transactionDate") {
                const dateValue = row[key];
                if (dateValue) {
                  let parsed = null;
                  if (dayjs.isDayjs(dateValue)) parsed = dateValue;
                  else if (dateValue instanceof Date) parsed = dayjs(dateValue);
                  else if (typeof dateValue === "number") {
                    const jsDate = new Date((dateValue - 25569) * 86400 * 1000);
                    parsed = dayjs(jsDate);
                  } else {
                    const formats = [
                      "DD-MM-YYYY HH:mm",
                      "YYYY-MM-DD HH:mm",
                      "YYYY-MM-DD",
                      "DD/MM/YYYY",
                      "MM/DD/YYYY",
                      "YYYY/MM/DD",
                      "DD-MM-YYYY",
                      "MM-DD-YYYY",
                      "DD.MM.YYYY",
                      "MM.DD.YYYY",
                    ];
                    let ok = null;
                    for (const f of formats) {
                      const test = dayjs(dateValue, f, true);
                      if (test.isValid()) {
                        ok = test;
                        break;
                      }
                    }
                    parsed = ok || dayjs(dateValue);
                  }
                  normalizedRow[mappedKey] = parsed?.isValid()
                    ? parsed.format("YYYY-MM-DD")
                    : String(dateValue);
                }
              } else if (mappedKey === "marketplace") {
                let marketplaceValue =
                  row[key] !== null && row[key] !== undefined
                    ? String(row[key]).trim()
                    : "";
                if (!isNaN(Number(marketplaceValue))) marketplaceValue = "";
                normalizedRow[mappedKey] = sanitizeText(marketplaceValue);
              } else if (mappedKey === "quantity") {
                normalizedRow[mappedKey] =
                  row[key] !== null && row[key] !== undefined
                    ? Number(row[key])
                    : 0;
              } else if (mappedKey === "totalPrice") {
                normalizedRow[mappedKey] =
                  row[key] !== null && row[key] !== undefined
                    ? Number(row[key])
                    : 0;
              } else if (mappedKey === "total") {
                normalizedRow[mappedKey] = parseCurrency(row[key]);
              } else {
                normalizedRow[mappedKey] = row[key];
              }
            });

            if (!foundMapped) {
              // allow fallback if at least SKU-like column is present
              const skuKey = keys.find((k) => {
                const nk = k.toLowerCase().trim();
                return (
                  nk === "sku" || nk === "product_sku" || nk === "item_sku"
                );
              });
              if (skuKey) {
                const skuValue = normalize(row[skuKey]);
                const sStock =
                  stockBySku.get(skuValue) ||
                  stockBySku.get(skuValue.replace(/^0+/, ""));
                normalizedRow.sku = skuValue;
                normalizedRow.barcode = sStock?.barcode
                  ? normalize(sStock.barcode)
                  : isLikelyBarcode(skuValue)
                  ? skuValue
                  : "";
                normalizedRow.quantity = 1;
                normalizedRow.transactionDate = dayjs().format("YYYY-MM-DD");
                normalizedRow.noBon = "";
                normalizedRow.marketplace = "";
                foundMapped = true;
              }
            }

            if (!foundMapped) return null;

            // ensure required defaults
            if (
              !Object.prototype.hasOwnProperty.call(normalizedRow, "quantity")
            ) {
              normalizedRow.quantity = 1;
            }
            if (
              !Object.prototype.hasOwnProperty.call(
                normalizedRow,
                "transactionDate"
              )
            ) {
              normalizedRow.transactionDate = dayjs().format("YYYY-MM-DD");
            }
            if (!Object.prototype.hasOwnProperty.call(normalizedRow, "noBon")) {
              normalizedRow.noBon = "";
            }
            if (
              !Object.prototype.hasOwnProperty.call(
                normalizedRow,
                "marketplace"
              )
            ) {
              normalizedRow.marketplace = "";
            }

            // sanitize free text
            normalizedRow.noBon = sanitizeText(normalizedRow.noBon);
            normalizedRow.keterangan = sanitizeText(normalizedRow.keterangan);

            return normalizedRow;
          })
          .filter(Boolean);

        if (!normalizedData.length) {
          throw new Error(
            "No valid data found after processing. Please check your column headers."
          );
        }

        // de-duplicate rows, then validate
        const deduped = dedupeRows(normalizedData);
        const validatedData = validateAllRows(deduped);

        setFileData(validatedData);
        setValidationResults(validatedData);
        message.success(`${file.name} uploaded successfully`);
      } catch (err) {
        console.error("Error parsing file:", err);
        message.error(`File upload failed: ${err.message || "Unknown error"}`);
      } finally {
        setParsing(false);
      }
    };

    reader.onerror = () => {
      setParsing(false);
      message.error("Failed to read the file. Please try again.");
    };

    try {
      reader.readAsArrayBuffer(file);
    } catch (error) {
      setParsing(false);
      console.error("Error reading file:", error);
      message.error(`Cannot read file: ${error.message || "Unknown error"}`);
    }

    return false; // stop default upload
  };

  /** ---- editing ---- */
  const isEditing = (record) => record.key === editingKey;

  const edit = (record) => {
    const stockItem = stockByBarcode.get(normalize(record.barcode));
    const price = stockItem ? Number(stockItem.harga_internal) || 0 : 0;

    let transactionDate = null;
    if (record.transactionDate) {
      transactionDate = dayjs.isDayjs(record.transactionDate)
        ? record.transactionDate
        : dayjs(record.transactionDate);
    } else {
      transactionDate = dayjs();
    }

    form.setFieldsValue({
      transactionDate,
      noBon: record.noBon || "",
      marketplace: record.marketplace || "",
      barcode: record.barcode,
      quantity: record.quantity,
      total: record.total || price * Number(record.quantity || 0),
      keterangan: record.keterangan || "",
    });

    setEditingKey(record.key);
  };

  const cancel = () => setEditingKey("");

  const handleDelete = (key) => {
    Modal.confirm({
      title: "Are you sure you want to delete this item?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        const newData = fileData.filter((item) => item.key !== key);
        setFileData(newData);
        setValidationResults(newData);
        const validCount = newData.filter((item) => item.isValid).length;
        message.success(
          `Item deleted. ${validCount}/${newData.length} valid items remaining.`
        );
      },
    });
  };

  const save = async (key) => {
    try {
      const row = await form.validateFields();
      const newData = [...fileData];
      const index = newData.findIndex((item) => key === item.key);
      if (index > -1) {
        const item = newData[index];
        const processedRow = { ...row };

        if (dayjs.isDayjs(processedRow.transactionDate)) {
          processedRow.transactionDate =
            processedRow.transactionDate.format("YYYY-MM-DD");
        }
        if (processedRow.total != null) {
          processedRow.total = parseCurrency(processedRow.total);
        }

        const updatedItem = { ...item, ...processedRow };
        const validation = validateRow(updatedItem);

        newData.splice(index, 1, {
          ...updatedItem,
          isValid: validation.isValid,
          errors: validation.errors,
          isBermasalah: validation.isBermasalah,
        });

        setFileData(newData);
        setValidationResults(newData);
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  /** ---- build payload ---- */
  const getArticleByPrefix = async (prefix) =>
    fetchArticleByBarcodePrefix(prefix);

  const buildTransactionData = async () => {
    const data = await Promise.all(
      fileData.map(async (item) => {
        const stockItem = stockByBarcode.get(normalize(item.barcode));
        if (!stockItem) return null;

        const barcodePrefix =
          normalize(stockItem.barcode).substring(0, 7) || "";
        let article = null;
        try {
          article = await getArticleByPrefix(barcodePrefix);
        } catch (e) {
          console.warn("Article fetch failed", e);
          return null;
        }

        const qty = Math.max(1, parseInt(item.quantity, 10) || 0);
        const price = determinePriceFromArticle(article, stockItem);
        const expectedTotal = price * qty;
        const inputTotal = parseCurrency(item.total);
        const total = inputTotal > 0 ? inputTotal : expectedTotal;

        const discountAmount = Math.max(0, expectedTotal - total);
        const discountPercentage =
          expectedTotal > 0
            ? Math.min(100, Math.round((discountAmount / expectedTotal) * 100))
            : 0;

        let transactionDate = null;
        const rawDate = item.transactionDate;
        if (
          typeof rawDate === "string" &&
          /^\d{4}-\d{2}-\d{2}$/.test(rawDate)
        ) {
          transactionDate = rawDate;
        } else if (rawDate instanceof Date || dayjs.isDayjs(rawDate)) {
          transactionDate = dayjs(rawDate).format("YYYY-MM-DD");
        } else if (typeof rawDate === "string") {
          const parsed = dayjs(
            rawDate,
            [
              "DD/MM/YYYY",
              "MM/DD/YYYY",
              "YYYY/MM/DD",
              "DD-MM-YYYY",
              "MM-DD-YYYY",
            ],
            true
          );
          if (parsed.isValid()) transactionDate = parsed.format("YYYY-MM-DD");
        }
        if (!transactionDate) transactionDate = dayjs().format("YYYY-MM-DD");

        const isBermasalah = qty > totalAvailable(stockItem.barcode);

        return {
          noBon: item.noBon || "",
          barcode: stockItem.barcode,
          marketplace: item.marketplace || "",
          sku: stockItem.sku,
          article: stockItem.article,
          size: stockItem.size,
          color: stockItem.color,
          price,
          quantity: qty,
          discount: discountPercentage,
          keterangan: item.keterangan || "",
          total,
          isBermasalah,
          soldBy: userData.name || "",
          counterCode: userData.counterCode || "",
          dept: userData.dept || "",
          transactionDate,
          hpp: stockItem ? Number(stockItem.price) || 0 : 0,
          ...(isBermasalah ? { bR: 1 } : {}),
        };
      })
    );

    return data.filter(Boolean);
  };

  /** ---- actions ---- */
  const handleDeleteAllInvalid = () => {
    const validFileData = fileData.filter(
      (_, index) => validationResults[index]?.isValid
    );
    const validValidationResults = validationResults.filter(
      (res) => res?.isValid
    );
    const invalidCount = fileData.length - validFileData.length;
    if (invalidCount === 0)
      return message.info("There are no invalid rows to delete.");
    setFileData(validFileData);
    setValidationResults(validValidationResults);
    message.success(`${invalidCount} invalid row(s) deleted.`);
  };

  const handleDeleteAll = () => {
    if (!fileData.length) return;
    Modal.confirm({
      title: "Delete All Items",
      content:
        "Are you sure you want to delete all items? This action cannot be undone.",
      okText: "Yes, Delete All",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        setFileData([]);
        setValidationResults([]);
        message.success("All items have been deleted.");
      },
    });
  };

  const handleConfirm = async () => {
    try {
      const { canProceed, message: errorMessage } =
        await checkInternetBeforeAPI();
      if (!canProceed) {
        message.error(errorMessage);
        return;
      }

      setSubmitting(true);

      const invalidRows = fileData.filter(
        (item) =>
          !item.transactionDate ||
          !item.noBon ||
          !item.marketplace ||
          !item.barcode ||
          !item.quantity
      );
      if (invalidRows.length > 0) {
        message.error(
          `${invalidRows.length} rows have missing required fields. Please check Transaction Date, Order ID, Marketplace, SKU, and Quantity.`
        );
        setSubmitting(false);
        return;
      }

      // warn if oversell exists
      if (fileData.some((r) => r.isBermasalah)) {
        const ok = await new Promise((resolve) =>
          Modal.confirm({
            title: "Some rows exceed available stock",
            content: "Proceed anyway?",
            okText: "Proceed",
            cancelText: "Cancel",
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          })
        );
        if (!ok) {
          setSubmitting(false);
          return;
        }
      }

      // warm article cache by prefixes first (avoid N+1 stalls)
      const prefixes = [
        ...new Set(fileData.map((r) => normalize(r.barcode).slice(0, 7))),
      ];
      await Promise.all(prefixes.map((p) => fetchArticleByBarcodePrefix(p)));

      const transactionData = await buildTransactionData();

      // Submit
      await createTransaction(transactionData);

      message.success(
        `${transactionData.length} items submitted successfully!`
      );
      onCancel();
    } catch (error) {
      const isDuplicateKey =
        error?.response?.data?.error?.code === 11000 ||
        error?.message?.includes("E11000") ||
        error?.response?.data?.message?.includes("E11000");

      if (isDuplicateKey) {
        console.warn("Duplicate key error ignored.");
      } else if (error?.response && error.response.status === 400) {
        console.error("Error submitting transaction:", error);
        message.error(
          error.response.data.message ||
            "Failed to submit transaction. Please check your input."
        );
      } else {
        console.error("Error submitting transaction:", error);
        message.error("Failed to submit transaction. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const workbook = XLSX.utils.book_new();
    const templateData = [
      {
        "Tanggal Pembuatan": "2025-06-08",
        "ID Pesanan": "ORDER123",
        Channel: "Shopee",
        SKU: "SAMPLE123",
        "Harga Total Promosi": 100000,
        Jumlah: 1,
        keterangan: "Sample note",
      },
    ];
    const worksheet = XLSX.utils.json_to_sheet(templateData);

    XLSX.utils.sheet_add_aoa(
      worksheet,
      [
        ["Template Instructions:"],
        [
          "1. Tanggal Pembuatan: Enter the transaction date (format: YYYY-MM-DD)",
        ],
        ["2. ID Pesanan: Enter the order ID or form number"],
        [
          "3. Channel: Enter the marketplace channel (e.g., Shopee, Tokopedia, etc.)",
        ],
        ["4. SKU: Enter the product SKU code (required)"],
        ["5. Harga Total Promosi: Enter the final price after promotions"],
        ["6. Jumlah: Enter quantity of items (required)"],
        ["7. keterangan: Enter any notes (optional)"],
        [],
        ["Example data is provided below:"],
      ],
      { origin: "A1" }
    );

    worksheet["!cols"] = [
      { wch: 20 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
      { wch: 20 },
      { wch: 10 },
      { wch: 30 },
    ];

    worksheet["!ref"] = "A1:G11";
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "transaction_bulk_template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    message.success("Template downloaded successfully");
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".xlsx,.xls,.csv",
    beforeUpload: handleUpload,
    showUploadList: false,
  };

  const validCount = validationResults.filter((item) => item.isValid).length;
  const totalCount = validationResults.length;
  const hasInvalidRows = validCount < totalCount;

  const handleModalClose = () => onCancel();

  const handleDownloadInvalidRows = () => {
    const invalidRows = fileData
      .filter((row) => !row.isValid)
      .map((r) => ({
        ...r,
        __errors: (r.errors || []).join("; "),
      }));

    if (invalidRows.length === 0) {
      message.warning("No invalid rows to download.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(invalidRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Invalid Rows");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "invalid_rows.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleResetData = () => {
    setShouldResetData(true);
    onCancel();
  };

  /** ---- columns & editable cells ---- */
  const skuOptions = useMemo(
    () =>
      (stockData || []).map((s) => ({
        value: s.barcode,
        label: `${s.sku} (${s.article} - ${s.color} - ${s.size})`,
        keywords: [s.sku, s.article, s.color, s.size]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      })),
    [stockData]
  );

  const columns = [
    {
      title: "Status",
      key: "status",
      width: 60,
      render: (_, record) => (
        <span>
          {record.isValid ? (
            <Tooltip title="Valid">
              <CheckCircleOutlined style={{ color: "green" }} />
            </Tooltip>
          ) : (
            <Tooltip title={(record.errors || []).join(", ") || "Invalid"}>
              <CloseCircleOutlined style={{ color: "red" }} />
            </Tooltip>
          )}
          {record.isBermasalah && (
            <Tooltip title="Quantity exceeds available stock">
              <WarningOutlined style={{ color: "orange", marginLeft: 8 }} />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: "Transaction Date",
      dataIndex: "transactionDate",
      key: "transactionDate",
      editable: true,
      render: (date) => {
        if (!date) return "-";
        const parsedDate = dayjs(date);
        return parsedDate.isValid() ? parsedDate.format("YYYY-MM-DD") : date;
      },
    },
    { title: "Order ID", dataIndex: "noBon", key: "noBon", editable: true },
    {
      title: "Marketplace",
      dataIndex: "marketplace",
      key: "marketplace",
      editable: true,
    },
    {
      title: "SKU",
      dataIndex: "barcode",
      key: "barcode",
      editable: true,
      render: (_, record) => {
        const stockItem = stockByBarcode.get(normalize(record.barcode));
        return stockItem?.sku || record.sku || record.barcode || "-";
      },
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      editable: true,
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      editable: true,
      render: (total, record) => {
        const formatRupiah = (value) =>
          new Intl.NumberFormat("id-ID", {
            style: "currency",
            currency: "IDR",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(parseCurrency(value));
        if (total) return formatRupiah(total);
        const stockItem = stockByBarcode.get(normalize(record.barcode));
        if (stockItem && record.quantity) {
          return formatRupiah(
            Number(stockItem.harga_internal) * Number(record.quantity)
          );
        }
        return formatRupiah(0);
      },
    },
    {
      title: "Keterangan",
      dataIndex: "keterangan",
      key: "keterangan",
      editable: true,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Button
              type="link"
              onClick={() => save(record.key)}
              style={{ marginRight: 8 }}
            >
              Save
            </Button>
            <Button type="link" onClick={cancel}>
              Cancel
            </Button>
          </span>
        ) : (
          <Space>
            <Button
              type="link"
              disabled={editingKey !== ""}
              onClick={() => edit(record)}
            >
              <EditOutlined /> Edit
            </Button>
            <Button
              type="link"
              danger
              disabled={editingKey !== ""}
              onClick={() => handleDelete(record.key)}
            >
              <CloseCircleOutlined /> Delete
            </Button>
          </Space>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) return col;

    let inputType = "text";
    if (col.dataIndex === "quantity" || col.dataIndex === "total")
      inputType = "number";
    else if (col.dataIndex === "transactionDate") inputType = "date";
    else if (col.dataIndex === "marketplace") inputType = "select";

    return {
      ...col,
      onCell: (record) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        inputType,
      }),
    };
  });

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    let inputNode;

    if (dataIndex === "barcode") {
      inputNode = (
        <Select
          placeholder="Select SKU"
          showSearch
          options={skuOptions}
          filterOption={(input, option) =>
            option?.keywords?.includes(input.toLowerCase())
          }
          allowClear
          style={{ width: "100%" }}
        />
      );
    } else if (inputType === "date" || dataIndex === "transactionDate") {
      const DatePickerWrapper = () => {
        let initialDate;
        if (form.getFieldValue(dataIndex)) {
          const formValue = form.getFieldValue(dataIndex);
          initialDate =
            typeof formValue === "string" ? dayjs(formValue) : formValue;
        } else if (record && record[dataIndex]) {
          initialDate = dayjs(record[dataIndex]);
        } else {
          initialDate = dayjs();
        }
        const [selectedDate, setSelectedDate] = useState(initialDate);

        useEffect(() => {
          const dateString = initialDate.format("YYYY-MM-DD");
          form.setFieldsValue({ [dataIndex]: dateString });
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return (
          <DatePicker
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            allowClear={false}
            value={selectedDate}
            onChange={(date) => {
              if (!date) return;
              setSelectedDate(date);
              const dateString = date.format("YYYY-MM-DD");
              form.setFieldsValue({ [dataIndex]: dateString });
            }}
            disabledDate={(current) => {
              if (!current) return false;
              const now = dayjs().endOf("day");
              const earliest = dayjs()
                .subtract(MAX_AGE_DAYS, "day")
                .startOf("day");
              return current.isBefore(earliest) || current.isAfter(now);
            }}
          />
        );
      };
      inputNode = <DatePickerWrapper />;
    } else if (inputType === "select" || dataIndex === "marketplace") {
      inputNode = (
        <Select
          placeholder="Select marketplace"
          style={{ width: "100%" }}
          options={[...ALLOWED_MARKETPLACES].map((m) => ({
            value: m,
            label: m,
          }))}
        />
      );
    } else if (inputType === "number") {
      if (dataIndex === "total") {
        inputNode = (
          <Input
            style={{ width: "100%" }}
            onChange={(e) => {
              const numValue = parseCurrency(e.target.value);
              form.setFieldsValue({ [dataIndex]: String(numValue) });
            }}
          />
        );
      } else {
        inputNode = <Input type="number" min={1} step={1} />;
      }
    } else {
      inputNode = <Input />;
    }

    const isRequired = [
      "barcode",
      "quantity",
      "transactionDate",
      "noBon",
      "marketplace",
    ].includes(dataIndex);

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              { required: isRequired, message: `Please input ${title}!` },
              dataIndex === "transactionDate"
                ? {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      if (
                        typeof value === "string" &&
                        /^\d{4}-\d{2}-\d{2}$/.test(value)
                      )
                        return Promise.resolve();
                      if (
                        value &&
                        typeof value === "object" &&
                        typeof value.format === "function"
                      )
                        return Promise.resolve();
                      return Promise.reject(new Error("Invalid date format"));
                    },
                  }
                : {},
              dataIndex === "quantity"
                ? {
                    validator: (_, v) => {
                      const n = Number(v);
                      if (!Number.isInteger(n) || n <= 0 || n > MAX_QTY)
                        return Promise.reject(
                          `Quantity must be integer 1..${MAX_QTY}`
                        );
                      return Promise.resolve();
                    },
                  }
                : {},
            ].filter((r) => Object.keys(r).length > 0)}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  return (
    <Modal
      title="Bulk Input"
      open={isVisible}
      onCancel={handleModalClose}
      width={800}
      footer={[
        <Button key="reset" onClick={handleResetData} style={{ float: "left" }}>
          Reset & Close
        </Button>,
        <Button
          key="cancel"
          onClick={handleModalClose}
          disabled={submitting || parsing}
        >
          Close
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          disabled={
            parsing || fileData.length === 0 || hasInvalidRows || submitting
          }
          loading={submitting}
        >
          {submitting ? "Submitting..." : "Confirm"}
        </Button>,
      ]}
    >
      {(submitting || parsing) && (
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          <p>
            {parsing
              ? "Parsing file. Please wait..."
              : "Processing transaction. Please wait..."}
          </p>
        </div>
      )}

      {fileData.length === 0 ? (
        <div>
          <div style={{ marginBottom: 16, textAlign: "right" }}>
            <Space>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownloadTemplate}
              >
                Download Template
              </Button>
            </Space>
          </div>

          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for a single Excel or CSV file upload.
              <br />
              Required columns: SKU (or "SKU"), Jumlah (or "Quantity")
              <br />
              Optional columns: Tanggal Pembuatan, ID Pesanan, Channel, Harga
              Total Promosi, keterangan
            </p>
          </Dragger>
        </div>
      ) : (
        <div>
          <div
            style={{
              marginBottom: 16,
              borderTop: "1px solid #f0f0f0",
              paddingTop: 16,
            }}
          >
            <Space
              direction="vertical"
              style={{ width: "100%", marginBottom: 16 }}
            >
              <Text>
                Valid rows: {validCount}/{totalCount}
              </Text>
              {hasInvalidRows && (
                <Text type="danger">
                  Please fix all errors before confirming
                </Text>
              )}
            </Space>
          </div>

          <Form form={form} component={false}>
            <Table
              components={{ body: { cell: EditableCell } }}
              bordered
              dataSource={fileData}
              columns={mergedColumns}
              rowClassName={(record) =>
                !record.isValid ? "ant-table-row-error" : ""
              }
              pagination={{ pageSize: 5, position: ["bottomCenter"] }}
              scroll={{ x: "max-content" }}
              size="small"
              rowKey={(r) => r.key}
            />
          </Form>

          <div
            style={{
              marginTop: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <Space wrap>
              <Button
                onClick={() => {
                  setFileData([]);
                  setValidationResults([]);
                }}
              >
                Upload Another File
              </Button>

              <Button
                danger
                icon={<DownloadOutlined />}
                onClick={handleDownloadInvalidRows}
              >
                Download Invalid Rows
              </Button>

              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteAllInvalid}
              >
                Delete All Invalid
              </Button>
            </Space>

            <Button
              danger
              onClick={handleDeleteAll}
              icon={<CloseCircleOutlined />}
            >
              Delete All
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BulkInputForm;
